import React, { useEffect, useRef, useState } from 'react';
import { prepareWithSegments, layoutWithLines } from '@chenglou/pretext';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './LiquidType.css';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const PROMPT = 'Pretext로 스크롤 위치에 따라 텍스트가 유체처럼 리플로우되는 원페이지. 뷰포트 너비를 스크롤 진행률에 따라 동적으로 변화시켜 텍스트 줄바꿈이 실시간으로 재배치되는 키네틱 타이포그래피 실험. 배경은 #14181E, 텍스트는 #00FF99.';

const BODY_TEXT = `The future arrives unevenly distributed. We build with code, we think in systems, we dream in pixels. Every scroll is a conversation between intent and output. Typography is not decoration — it is the voice of thought made visible. When text reflows, meaning shifts. When meaning shifts, perception changes. This is the experiment: can constraint become expression? Two colors. One typeface. Infinite reflow. The width narrows and the words cascade, finding new rhythm in tighter spaces. What was one line becomes three. What was a whisper becomes a shout. Pretext measures each glyph with sub-pixel precision, bypassing the DOM entirely. No reflow jank. No layout thrashing. Just pure, fluid text — reshaping itself sixty times per second as you scroll. Watch the lines breathe. Notice how meaning pools in different configurations. The same words, the same order, but an entirely different texture. This is kinetic typography in its most essential form: not animation for spectacle, but transformation as meaning. Scroll deeper. The container shrinks. Letters crowd together, then expand again, like lungs filling with air. Each width is a new composition. Each reflow is a new poem written from the same vocabulary. Zero limits.`;

const LiquidType: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const hintRef = useRef<HTMLDivElement>(null);
  const [scrollPct, setScrollPct] = useState(0);
  const [currentWidth, setCurrentWidth] = useState(0);
  const [lineCount, setLineCount] = useState(0);
  const exp = experiments.find(e => e.id === 'liquid-type');

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let lastScroll = -1;
    let hasScrolled = false;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const render = () => {
      animRef.current = requestAnimationFrame(render);

      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.max(0, Math.min(1, scrollTop / docHeight)) : 0;

      // Skip if no scroll change
      if (Math.abs(scrollTop - lastScroll) < 0.5) return;
      lastScroll = scrollTop;

      if (!hasScrolled && scrollTop > 10) {
        hasScrolled = true;
        if (hintRef.current) hintRef.current.style.opacity = '0';
      }

      const w = window.innerWidth;
      const h = window.innerHeight;

      // Dynamic width: oscillates based on scroll
      // Wide → narrow → wide → narrow pattern
      const phase = progress * Math.PI * 4; // 2 full cycles
      const widthRatio = 0.3 + 0.5 * (0.5 + 0.5 * Math.cos(phase)); // 0.3 to 0.8
      const maxTextWidth = w * widthRatio;

      setScrollPct(Math.round(progress * 100));
      setCurrentWidth(Math.round(maxTextWidth));

      // Clear
      ctx.clearRect(0, 0, w, h);

      // Font setup
      const baseFontSize = Math.min(w * 0.022, 18);
      const lineHeight = baseFontSize * 1.7;
      const font = `${baseFontSize}px "Press Start 2P", monospace`;

      try {
        const prepared = prepareWithSegments(BODY_TEXT, font);
        const result = layoutWithLines(prepared, maxTextWidth, lineHeight);

        setLineCount(result.lineCount);

        // Calculate vertical centering
        const totalHeight = result.height;
        const startY = Math.max(40, (h - totalHeight) / 2);
        const startX = (w - maxTextWidth) / 2;

        // Draw each line
        result.lines.forEach((line, i) => {
          const y = startY + i * lineHeight;

          // Only render visible lines
          if (y < -lineHeight || y > h + lineHeight) return;

          // Fade edges
          const edgeFade = Math.min(
            (y - 20) / 60,
            (h - 40 - y) / 60
          );
          const alpha = Math.max(0, Math.min(1, edgeFade));

          // Slight wave offset per line based on scroll
          const waveOffset = Math.sin(progress * Math.PI * 6 + i * 0.3) * 3;

          // Color intensity varies per line
          const intensity = 0.5 + 0.5 * Math.sin(progress * Math.PI * 2 + i * 0.15);
          const green = Math.round(200 + intensity * 55);

          ctx.font = font;
          ctx.fillStyle = `rgba(0, ${green}, ${Math.round(153 * (0.6 + intensity * 0.4))}, ${alpha * (0.6 + intensity * 0.4)})`;
          ctx.textBaseline = 'top';
          ctx.fillText(line.text, startX + waveOffset, y);
        });

        // Draw container boundary (subtle)
        ctx.strokeStyle = `rgba(0, 255, 153, ${0.05 + progress * 0.05})`;
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 8]);
        ctx.strokeRect(startX - 8, startY - 8, maxTextWidth + 16, totalHeight + 16);
        ctx.setLineDash([]);

        // Width label
        ctx.font = `10px "Press Start 2P", monospace`;
        ctx.fillStyle = 'rgba(0, 255, 153, 0.2)';
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(maxTextWidth)}px`, w / 2, startY - 20);
        ctx.textAlign = 'start';

      } catch {
        // Fallback: just render raw text
        ctx.font = font;
        ctx.fillStyle = '#00FF99';
        ctx.fillText('Loading Pretext...', 40, h / 2);
      }
    };

    window.addEventListener('resize', resize);
    document.fonts.ready.then(() => {
      resize();
      animRef.current = requestAnimationFrame(render);
    });

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div ref={containerRef} className="liquidType">
      <canvas ref={canvasRef} className="liquidCanvas" />

      <ExperimentNav prompt={exp?.prompt || PROMPT} title="Liquid Type" />

      {/* Width indicator bar at top */}
      <div
        className="widthIndicator"
        style={{ width: `${scrollPct}%` }}
      />

      {/* Scroll track */}
      <div className="scrollTrack">
        <div
          className="scrollThumb"
          style={{ top: `${scrollPct}%` }}
        />
      </div>

      <div ref={hintRef} className="liquidHint">scroll to reflow</div>

      <div className="liquidHud">
        <span>scroll: <span className="hudVal">{scrollPct}%</span></span>
        <span>width: <span className="hudVal">{currentWidth}px</span></span>
        <span>lines: <span className="hudVal">{lineCount}</span></span>
        <span>engine: <span className="hudVal">pretext</span></span>
      </div>
    </div>
  );
};

export default LiquidType;
